import type { ProjectOption } from "../backend/api/projects";
import type { StatusTone } from "../design/tokens";

// Status colour resolves to the shared tone tokens, so project status looks the
// same as every other status in the product and follows the active theme.
export const PROJECT_TONE_CLASSES: Record<StatusTone, string> = {
  neutral: "text-muted-foreground",
  info: "text-tone-info-foreground",
  warning: "text-tone-warning-foreground",
  success: "text-tone-success-foreground",
  danger: "text-tone-danger-foreground",
};

const PROJECT_STATUS_TONES: Record<string, StatusTone> = {
  "In-Progress": "info",
  "On Hold": "warning",
  Completed: "success",
  Cancelled: "danger",
};

export function findProjectOption(
  options: ProjectOption[],
  code?: string | null,
): ProjectOption | null {
  return options.find((option) => option.code === code) || null;
}

export function labelForProjectOption(
  options: ProjectOption[],
  code?: string | null,
): string {
  return findProjectOption(options, code)?.label || code || "";
}

/** The semantic tone a project status belongs to. */
export function toneForProjectStatus(code?: string | null): StatusTone {
  return (code && PROJECT_STATUS_TONES[code]) || "neutral";
}

/** Text colour for places that show a status as plain text rather than a badge. */
export function toneClassForProjectStatus(code?: string | null): string {
  return PROJECT_TONE_CLASSES[toneForProjectStatus(code)];
}

// Historical records may contain values that predate the current domain
// contract. Keep the current value visible while preventing it from being
// offered for new records.
export function optionsIncludingCurrent(
  options: ProjectOption[],
  currentCode?: string | null,
): ProjectOption[] {
  if (!currentCode || options.some((option) => option.code === currentCode)) {
    return options;
  }

  return [
    ...options,
    {
      code: currentCode,
      label: currentCode,
    },
  ];
}
